// ============================================================================
// chatMessageLayout.ts — ЕДИНЫЙ расчёт габаритов сообщения (SSoT/SRP).
//
// Причина: пузырь `max-w-[78%]` + одиночная картинка `w-full h-64 object-cover`
// растягивали сообщение на ширину ленты и «зумили» изображение (обрезка +
// фиксированная высота). Здесь ширина пузыря ограничена В ПИКСЕЛЯХ:
//   • текст/подпись — до 560px;
//   • медиа (картинки, видео, аудио) — до 460px;
//   • на узких экранах — по ширине ленты минус колонка аватара (32px + 6px).
// Один и тот же расчёт применяют MessageBubble и лента (ChatThread/embed),
// поэтому внешний вид одинаков для внутреннего чата и внешних каналов —
// источник сообщения (если есть) влияет только на бейдж, не на раскладку.
// ============================================================================

import type { CSSProperties } from 'react';

/** Максимальная ширина пузыря с текстом. */
export const MESSAGE_TEXT_MAX_WIDTH_PX = 560;
/** Максимальная ширина пузыря с медиа-вложениями. */
export const MESSAGE_MEDIA_MAX_WIDTH_PX = 460;
/** Внутренняя ширина медиа-пузыря (за вычетом px-3 = 24px). */
export const MESSAGE_MEDIA_INNER_MAX_WIDTH_PX = MESSAGE_MEDIA_MAX_WIDTH_PX - 24;
/** Предел высоты одиночной картинки/видео (на низких экранах — меньше). */
export const MESSAGE_SINGLE_MEDIA_MAX_HEIGHT = 'min(360px, 55vh)';
/** Тот же предел числом — для расчёта пропорций до отрисовки картинки. */
export const MESSAGE_SINGLE_MEDIA_MAX_HEIGHT_PX = 360;
/** Колонка аватара сообщения: h-8 w-8 (32px) + gap-1.5 (6px). */
export const MESSAGE_AVATAR_GUTTER_PX = 38;
/** Минимальная ширина медиа-пузыря: время/подпись не «схлопываются». */
export const MESSAGE_MEDIA_MIN_WIDTH_PX = 160;

/**
 * - `text` — только текст/файлы (сжимается по содержимому);
 * - `media` — видео/аудио/сетка картинок (стабильная ширина, без прыжков);
 * - `single-image` — одно изображение (пузырь по пропорции картинки).
 */
export type MessageBubbleLayout = 'text' | 'media' | 'single-image';

/** Предел ширины с учётом колонки аватара — не ломает мобильную ленту. */
function widthLimit(capPx: number): string {
  return `min(${capPx}px, calc(100% - ${MESSAGE_AVATAR_GUTTER_PX}px))`;
}

export function messageBubbleStyle(layout: MessageBubbleLayout): CSSProperties {
  const minWidth = `min(${MESSAGE_MEDIA_MIN_WIDTH_PX}px, 100%)`;
  if (layout === 'media') {
    const width = widthLimit(MESSAGE_MEDIA_MAX_WIDTH_PX);
    return { maxWidth: width, width, minWidth };
  }
  if (layout === 'single-image') {
    const maxWidth = widthLimit(MESSAGE_MEDIA_MAX_WIDTH_PX);
    return { maxWidth, width: 'fit-content', minWidth };
  }
  return { maxWidth: widthLimit(MESSAGE_TEXT_MAX_WIDTH_PX) };
}

/**
 * Ширина встроенного превью ссылки (AppEmbed) в ленте: та же медиа-полоса,
 * что у вложений — превью не растягивается на весь экран.
 */
export function messageEmbedStyle(): CSSProperties {
  return { maxWidth: `min(${MESSAGE_MEDIA_MAX_WIDTH_PX}px, calc(100% - ${MESSAGE_AVATAR_GUTTER_PX}px))` };
}
